import React, { Fragment, memo, useState } from 'react';
import MUIDataTable from 'mui-datatables';
import CustomSearchBar from '../../components/CustomSearchBar';
import EditableTextCell from './EditableTextCell';
import { LoanService } from '../../services';
import { centsToDollar, sortDollarColumn, aprToPercent, promosToChips } from '../../utils/formatters';
import { isHeldCell, dealerNeedsReviewCell, isConciergeCell, isRuleFailureCell } from '../../utils/cellRenderers';
import { saveTableSession, getTableSession } from '../../utils/sessionHelpers';
import { customTableSearch } from '../../utils/tableHelpers.js';
import { isSuccessfulResponse } from '../../utils/responseHelpers';
import { FlagCell, DealerMetricTooltip, LoanMetricTooltip, BorrowerMetricTooltip, CommentPreviewTooltip, LoanTypeIcon, FincenIdCell } from '../../components';
import AddHoldRequestButtons from './AddHoldRequestButtons';
import RepullButton from './RepullButton';
import { Tooltip, IconButton } from '@material-ui/core';
import { useRole } from '../../contexts/RolesContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useError } from '../../contexts/ErrorContext';
import ConfirmationModal from '../../components/ConfirmationModal';
import DeleteFundingRequestsButton from './DeleteFundingRequestsButton';
import red from '@material-ui/core/colors/red';

import {
    CloudDownload,
    Add as AddIcon,
    WarningRounded as WarningRoundedIcon,
} from '@material-ui/icons';

const tablifyFundingRequests = fundingRequests => fundingRequests == null ? null : fundingRequests.map(fundingRequest => {
    return [
        fundingRequest.id,
        fundingRequest.loan.loanNumber,
        fundingRequest.loan.dealer.dealerNumber,
        fundingRequest.loan.dealer.name,
        centsToDollar(fundingRequest.amountCents),
        (fundingRequest.loan.hasStagedFunding === true ? 'Yes' : 'No'),
        fundingRequest.status === 'held',
        fundingRequest.loan.dealer.organization,
        fundingRequest.loan.commentsCount,
        fundingRequest.loan.multipleActiveRequests,
        fundingRequest.loan.dealer.needsReview,
        fundingRequest.loan.dealer.isManualFlagged,
        fundingRequest.loan.lender.shortName,
        fundingRequest.loan.applicant.fullName,
        fundingRequest.loan.dealer.isConcierge,
        fundingRequest.loan.notes,
        fundingRequest.loan.dealer.dealerMetric.totalFundedLoans,
        fundingRequest.loan.dealer.dealerMetric.maxLoanAmount,
        fundingRequest.loan.dealer.dealerMetric.totalLoanAmount,
        fundingRequest.loan.dealer.dealerMetric.averageLoanAmount,
        centsToDollar(fundingRequest.loan.estimatedProjectAmountCents),
        centsToDollar(fundingRequest.loan.totalLoanAmountCents),
        fundingRequest.loan.termMonths,
        aprToPercent(fundingRequest.loan.apr),
        fundingRequest.loan.dealer.manualFlaggedReason,
        fundingRequest.loan.dealer.dealerMetric.firstFundingDate,
        fundingRequest.comingOffHold ? "Yes" : "No",
        fundingRequest.loan.holdComment,
        fundingRequest.loan.dealer.dealerMetric.activeLoansCount,
        fundingRequest.loan.dealer.dealerMetric.delinquentLoansCount,
        promosToChips(fundingRequest.loan),
        fundingRequest.loan.projectType,
        fundingRequest.loan.applicant.creditScore,
        fundingRequest.loan.applicant.employer,
        aprToPercent(fundingRequest.debtIncomeRatio),
        centsToDollar(fundingRequest.totalIncome),
        fundingRequest.loan.coapplicant?.fullName,
        fundingRequest.loan.coapplicant?.employer,
        fundingRequest.loan.coapplicant?.creditScore,
        fundingRequest.applicationDate,
        fundingRequest.loan.dealer.dealerMetric.lastFundingDate,
        fundingRequest.failureCount ? fundingRequest.failureCount : 0,
        fundingRequest.loan.loanType,
        fundingRequest.address,
        fundingRequest.loan.dealer.dealerMetric.delinquentLoansOverThirtyDaysCount,
        fundingRequest.loan.comments,
        fundingRequest.loan.dealer.hasFincenId,
        fundingRequest.loan.applicant.occupation,
        fundingRequest.loan.coapplicant?.occupation,
        null,
        fundingRequest.loan.dealer.dealerMetric.allTimeAverageCreditScorePb,
        fundingRequest.loan.dealer.dealerMetric.lastYearAverageCreditScorePb,
    ];
});

const FundingRequestsTable = ({ fundingRequests, selectedDate, toggleShowNewFundingRequest }) => {
    const [selectedRowIndices, setSelectedRowsIndices] = useState([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const { isDailyProcessingWrite, isAdmin } = useRole();
    const { createNotification, createErrorNotification } = useNotification();
    const { showError } = useError();
    const starterColumns = ['Loan ID', 'Dealer #', 'Dealer', 'Amount', 'Staged Funding', 'Payment Stage', 'Completed Payments', 'CU Pull', 'Org', 'On Hold?','Dealer Account Flag', 'Manual Flag', 'FinCEN ID', 'Lender', 'Notes', 'Total Funded Loans', 'Loan Type'];
    const [visibleColumns, setVisibleColumns] = useState((sessionStorage.getItem("fundingRequestsTable")) ? getTableSession("fundingRequestsTable") : saveTableSession(starterColumns, "fundingRequestsTable"));
    const [matchExactText, setmatchExactText] = useState(true);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const handleMatchExactText = (event) => {
        setmatchExactText(event.target.checked);
    }

    const handleAddFundingRequestClick = () => {
        toggleShowNewFundingRequest();
    };

    const handleDownloadLenderReportsClick = () => {
        LoanService.downloadFundingRequestReport(selectedDate).then(response => {
            if (isSuccessfulResponse(response)) {
                createNotification('Report requested successfully, check your email in a few minutes to download the file(s)');
            } else {
                createErrorNotification('Failed to request report download. Please try again later.');
            }
        }).catch(error => {
            createErrorNotification('Failed to request report download. Please try again later.');
        });
    };

    const handlePullClick = () => {
        LoanService.pullFundingRequests(selectedDate).then(response => {
            // !FIXME: Do this better!
            setTimeout(() => window.location.reload(), 10000);
        }).catch(showError);
        toggleConfirmation();
    };

    const toggleConfirmation = () => {
        setShowConfirmation(!showConfirmation);
    }

    const columns = [
        {
            name: 'Funding Request ID',
            options: {
                filter: false,
                display: false,
                viewColumns: false,
            }
        },
        {
            name: 'Loan ID',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('Loan ID') : true,
                customBodyRender: (value, tableMeta) => {
                    return <React.Fragment>
                        <div style={{display: 'flex'}}>
                            <LoanMetricTooltip
                                loanId={value}
                                estimatedProjectAmount={tableMeta.rowData[20]}
                                totalLoanAmount={tableMeta.rowData[21]}
                                loanTerm={tableMeta.rowData[22]}
                                loanApr={tableMeta.rowData[23]}
                                promos={tableMeta.rowData[30]}
                                projectType={tableMeta.rowData[31]}
                                applicationDate={tableMeta.rowData[39]}
                            />
                            <BorrowerMetricTooltip
                                borrowerName={tableMeta.rowData[13]}
                                borrowerCreditScore={tableMeta.rowData[32]}
                                borrowerDTI={tableMeta.rowData[34]}
                                borrowerAddress={tableMeta.rowData[43]}
                                borrowerEmployer={tableMeta.rowData[33]}
                                borrowerIncome={tableMeta.rowData[35]}
                                borrowerOccupation={tableMeta.rowData[47]}
                                coborrowerName={tableMeta.rowData[36]}
                                coborrowerCreditScore={tableMeta.rowData[38]}
                                coborrowerEmployer={tableMeta.rowData[37]}
                                coborrowerOccupation={tableMeta.rowData[48]}
                            />
                        </div>
                    </React.Fragment>
                }
            }
        },
        {
            name: 'Dealer #',
            options: {
                display: visibleColumns ? visibleColumns.includes('Dealer #') : true,
                filter: false,
                customBodyRender: (value, tableMeta) => {
                    return <a href={`/dealers/${value}`}>{value}</a>
                }
            }
        },
        {
            name: 'Dealer',
            options: {
                display: visibleColumns ? visibleColumns.includes('Dealer') : true,
                customBodyRender: (value, tableMeta) => {
                    return <DealerMetricTooltip
                        dealerName={value}
                        totalFundedLoans={tableMeta.rowData[16]}
                        maxLoanAmount={centsToDollar(tableMeta.rowData[17])}
                        totalLoanAmount={centsToDollar(tableMeta.rowData[18])}
                        averageLoanAmount={centsToDollar(tableMeta.rowData[19])}
                        firstFundingDate={tableMeta.rowData[25]}
                        lastFundingDate={tableMeta.rowData[40]}
                        dealerConcierge={tableMeta.rowData[14] ? "Yes" : "No"}
                        dealerDelinquentLoans={tableMeta.rowData[29] == null ? 0 : tableMeta.rowData[29]}
                        dealerThirtyDayDelinquentLoansCount={tableMeta.rowData[44] == null ? 0 : tableMeta.rowData[44] }
                        dealerAllTimeAverageCreditScore={tableMeta.rowData[50] == null ? 0 : tableMeta.rowData[50]}
                        dealerLastYearAverageCreditScore={tableMeta.rowData[51] == null ? 0 : tableMeta.rowData[51]}
                    />
                }
            }
        },
        {
            name: 'Amount',
            options: {
                display: visibleColumns ? visibleColumns.includes('Amount') : true,
                filter: false,
                sortCompare: sortDollarColumn
            }
        },
        {
            name: 'Staged Funding',
            options: {
                display: visibleColumns ? visibleColumns.includes('Staged Funding') : true,
                customFilterListOptions: {
                    render: v => v === "No" ? "Non-Staged Funded" : "Staged Funded",
                },
            }
        },
        {
            name: 'On Hold?',
            options: {
                display: visibleColumns ? visibleColumns.includes('On Hold?') : true,
                customBodyRender: isHeldCell,
                customFilterListOptions: {
                    render: v => v ? "On Hold" : "Not On Hold",
                },
            }
        },
        {
            name: 'Org',
            options: {
                display: visibleColumns ? visibleColumns.includes('Org') : true,
            }
        },
        {
            name: 'Comments',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('Comments') : true,
                customBodyRender: (value, tableMeta) => {
                    return (parseInt(value) === 0) ? null : (<CommentPreviewTooltip count={value} comments={tableMeta.rowData[45]}  />)
                }
            }
        },
        {
            name: 'Duplicate Funding Requests',
            options: {
                display: visibleColumns ? visibleColumns.includes('Duplicate Funding Requests') : true,
                customBodyRender: (value, tableMeta) => {
                    return (
                        value
                        ?
                            <Tooltip title={'This loan contains multiple active FRs'}>
                                <IconButton component="span">
                                    <WarningRoundedIcon style={{ color: red[500] }} />
                                </IconButton>
                            </Tooltip>
                        : null
                    );
                }
            }
        },
        {
            name: 'Dealer Account Flag',
            options: {
                display: visibleColumns ? visibleColumns.includes('Dealer Account Flag') : true,
                customBodyRender: dealerNeedsReviewCell,
                customFilterListOptions: {
                    render: v => v ? "Dealer Flagged" : "Dealer Not Flagged",
                },
            }
        },
        {
            name: 'Manual Flag',
            options: {
                display: visibleColumns ? visibleColumns.includes('Manual Flag') : true,
                // Note: dealerNumber comes from diff column on each table...that's why this isn't in cellRenderers
                customBodyRender: (value, tableMeta) => {
                    return (
                        <FlagCell
                            value={value}
                            dealerNumber={tableMeta.rowData[2]}
                            flagReason={tableMeta.rowData[24]}
                            disableUpdates={!isDailyProcessingWrite()}
                        />
                    );
                },
                customFilterListOptions: {
                    render: v => v ? "Manual Flagged" : "Non-Manual Flagged",
                },
            }
        },
        {
            name: 'Lender',
            options: {
                display: visibleColumns ? visibleColumns.includes('Lender') : true,
            }
        },
        {
            name: 'Borrower',
            options: {
                display: visibleColumns ? visibleColumns.includes('Borrower') : true,
            }
        },
        {
            name: 'Concierge Dealer',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('Concierge Dealer') : true,
                customBodyRender: isConciergeCell,
            }
        },
        {
            name: 'Notes',
            options: {
                setCellProps: () => ({ style: { minWidth: "250px" }}),
                display: visibleColumns ? visibleColumns.includes('Notes') : true,
                customBodyRender: (value, tableMeta) => {
                    return (
                        isDailyProcessingWrite() || isAdmin() ?
                        <EditableTextCell
                            value={value}
                            id={tableMeta.rowData[1]}
                            label={'Funding Request Notes'}
                            onSave={LoanService.updateLoanNotes} /> : value
                    );
                }
            }
        },
        {
            name: 'Total Funded Loans',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('Total Funded Loans') : true,
            }
        },
        {
            name: 'maxLoanAmount',
            options: {
                filter: false,
                display: false,
                viewColumns: false,
            }
        },
        {
            name: 'totalLoanAmount',
            options: {
                filter: false,
                display: false,
                viewColumns: false,
            }
        },
        {
            name: 'averageLoanAmount',
            options: {
                filter: false,
                display: false,
                viewColumns: false,
            }
        },
        {
            name: 'estimatedProjectAmount',
            options: {
                filter: false,
                display: false,
                viewColumns: false,
            }
        },
        {
            // This comes from loan table not FR table
            name: 'hiddenTotalLoanAmount',
            options: {
                filter: false,
                display: false,
                viewColumns: false,
            }
        },
        {
            name: 'term',
            options: {
                filter: false,
                display: false,
                viewColumns: false,
            }
        },
        {
            name: 'apr',
            options: {
                filter: false,
                display: false,
                viewColumns: false,
            }
        },
        {
            name: 'Manual Flag Reason',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('Manual Flag Reason') : true,
                viewColumns: true
            }
        },
        {
            name: 'Dealer First Funding Date',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Coming Off Hold',
            options: {
                display: visibleColumns ? visibleColumns.includes('Coming Off Hold') : true,
                customFilterListOptions: {
                    render: v => v === "No" ? "Not Coming off Hold" : "Coming off Hold",
                },
            }
        },
        {
            name: 'Hold Comment',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('Hold Comment') : true,
            }
        },
        {
            name: 'Dealer Loan Number',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Dealer Delinquency Number',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Promos',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Project Type',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('Project Type') : true,
                viewColumns: true,
                setCellProps: () => ({ style: { minWidth: "250px" }}),
                customBodyRender: (value, tableMeta) => {
                    return ( (isDailyProcessingWrite() || isAdmin()) ?
                        <EditableTextCell
                        value={value}
                        id={tableMeta.rowData[1]}
                        label={'Project Type'}
                        onSave={LoanService.updateFundingRequestProjectType} /> : value
                    );
                }
            }
        },
        {
            name: 'borrowerCreditScore',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'borrowerEmployer',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'borrowerDTI',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'borrowerIncome',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Coborrower',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'coborrowerEmployer',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'coborrowerCreditScore',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Application Date',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('Application Date') : true,
                viewColumns: true
            }
        },
        {
            name: 'Dealer Last Funding Date',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Rules Failed',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('Rules Failed') : true,
                viewColumns: true,
                customBodyRender: isRuleFailureCell,
            }
        },
        {
            name: 'Loan Type',
            options: {
                filter: true,
                display: visibleColumns ? visibleColumns.includes('Loan Type') : true,
                sort: false,
                viewColumns: true,
                filterOptions: {
                    names: ['Home Improvement', 'Healthcare', 'Solar'],
                },
                customBodyRender: (value) => {
                    return(
                        <div style={{cursor: 'default'}}>
                            <LoanTypeIcon loanType={value} />
                        </div>
                    )
                },
            }
        },
        {
            name: 'Borrower Address',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Delinquent for Over 30 Days Loan Count',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Comment Messages',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'FinCEN ID',
            options: {
                filter: false,
                display: visibleColumns ? visibleColumns.includes('FinCEN ID') : true,
                customBodyRender: (value, tableMeta) => {
                    return <FincenIdCell value={tableMeta.rowData[46]} disableUpdates={true} />
                },
                customFilterListOptions: {
                    render: v => v ? "With FinCEN ID" : "Without FinCEN ID",
                },
            }
        },
        {
            name: 'Borrower Occupation',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'CoBorrower Occupation',
            options: {
                filter: false,
                display: false,
                viewColumns: false
            }
        },
        {
            name: 'Alerts',
            options: {
                filter: false,
                display: false,
                viewColumns: false,
                customBodyRender: (value) => value ? ''  : 'N/A'
          }
        },
        {
            name: 'All Time Average Credit Score',
            options: {
                filter: false,
                display: false,
                viewColumns: true
            }
        },
        {
            name: 'Last Year Average Credit Score',
            options: {
                filter: false,
                display: false,
                viewColumns: true
            }
        }
    ];

    const options = {
        filterType: 'dropdown',
        print: false,
        download: false,
        searchOpen: true,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [50,100,250],
        setRowProps: (row, dataIndex, rowIndex) => {
            if (rowIndex % 2 === 0) {
                return {
                    style: { background: 'snow' }
                };
            }
        },
        rowsSelected: selectedRowIndices,
        onRowSelectionChange: (rowsSelected, allRows) => {
            setSelectedRowsIndices(allRows.map(row => row.dataIndex));
        },
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'viewColumnsChange':
                    let displayedVisibleColumns = tableState.columns.map(column => {
                        if (column.display === "true") {
                            return column.name;
                        } else {
                            return null;
                        }
                    }).filter(column => column !== null);

                    if (sessionStorage.getItem("fundingRequestsTable")) {
                        saveTableSession(displayedVisibleColumns, "fundingRequestsTable");
                    }
                    setVisibleColumns(displayedVisibleColumns);

                    break;
                default:
                    break;
            }
        },
        selectToolbarPlacement: 'none',
        textLabels: {
            toolbar: {
                downloadCsv: 'Download Lender Reports',
            },
        },
        customToolbar: () => {
            const selectedDateIsToday = new Date(selectedDate).toDateString() === new Date().toDateString();

            return isDailyProcessingWrite() ? (
                <Fragment>
                    {
                        selectedDateIsToday
                        ? <RepullButton hasItems={fundingRequests.length > 0}
                                        pullName={'Funding Requests'}
                                        onClick={toggleConfirmation}
                                        selectedDate={selectedDate} />
                        : null
                    }

                    {
                        selectedRowIndices.length > 0
                        ? (
                            <Fragment>
                                <AddHoldRequestButtons selectedRowIndices={selectedRowIndices} potentialHolds={fundingRequests} isFundingRequests={true} />
                                <DeleteFundingRequestsButton selectedRowIndices={selectedRowIndices} potentialDeletions={fundingRequests} date={selectedDate}  />
                            </Fragment>
                        )
                        : (
                            selectedDateIsToday
                            ? (
                                <Tooltip title={'Add a new funding request'}>
                                    <IconButton onClick={handleAddFundingRequestClick}>
                                        <AddIcon/>
                                    </IconButton>
                                </Tooltip>
                            )
                            : null
                        )
                    }

                    {
                        <Tooltip title={'Download Lender Reports'}>
                            <IconButton onClick={handleDownloadLenderReportsClick}>
                                <CloudDownload />
                            </IconButton>
                        </Tooltip>
                    }
                </Fragment>
            ) : null;
        },
        customSearchRender: (searchText, handleSearch, hideSearch, options) => {
            return (
                <CustomSearchBar
                    searchText={searchText}
                    handleSearch={handleSearch}
                    hideSearch={hideSearch}
                    options={options}
                    matchExactText={matchExactText}
                    handleMatchExactText={handleMatchExactText}
                />
            );
        },
        customSearch: (searchQuery, currentRow, columns) => {
            return customTableSearch(searchQuery, currentRow, columns, matchExactText);
        },
        onChangeRowsPerPage: (rowNumber) => {
            setRowsPerPage(rowNumber);
        },
    };

    return fundingRequests === null ? (
        <h1>Loading...</h1>
    ) : (
        <Fragment>
            <ConfirmationModal
                title="Confirm"
                message={`Are you sure you want to pull funding requests for ${new Date(selectedDate).toLocaleDateString()}?`}
                showConfirmationBox={showConfirmation}
                onConfirm={handlePullClick}
                onCancel={toggleConfirmation}
            />
            <MUIDataTable data={tablifyFundingRequests(fundingRequests)} columns={columns} options={options} />
        </Fragment>
    );
}

export default memo(FundingRequestsTable);
